import { takeLatest, put, call, select, race, delay } from 'redux-saga/effects';
import { getSearchResults, getSearchResultsFromBR } from 'yoda-interfaces/lib/Search/SearchApi';
import { getConstructorAutocompleteResults } from 'yoda-interfaces/lib/Search/ConstructorIO/SearchApi';
import _get from 'lodash/get';
import productSearchActionTypes from '../actionTypes/ProductSearchActionTypes';
import { triggerFormError } from '../actions/AnalyticsAction';

export const context = (state) => state.context;

function* productSearchSaga(action) {
    try {
        const contextData = yield select(context);
        const productSearchResults = yield call(
            getSearchResults,
            action.params,
            action.regionZone,
            action.version,
            contextData.featureFlags.disableNewAutoSuggestion
        );
        yield put({
            type: productSearchActionTypes.PRODUCTSEARCH_GET_SUCCESS,
            productSearchResults,
        });
    } catch (error) {
        yield put({
            type: productSearchActionTypes.PRODUCTSEARCH_GET_ERROR,
            error,
        });
    }
}

const getDepartment = (suggestion, searchTerm, orignalQuery) => {
    let department = '';
    const suggestionData = suggestion?.attributeSuggestions?.filter((item, index) => index <= 1);
    if (suggestionData?.length > 0) {
        suggestionData?.forEach((item) => {
            department = [
                ...department,
                {
                    name: item?.value || '',
                    href:
                        `/s?searchType=Predictive+Search&searchTerm=${searchTerm}&item_type=${encodeURIComponent(
                            item.value
                        )?.replaceAll('%20', '+')}&predSearchTerm=${orignalQuery}` || '',
                    products: [],
                },
            ];
        });
    }
    return department;
};

function* getData(productBRSearchResults) {
    if (!productBRSearchResults?.data) {
        return {};
    }
    const contextData = yield select(context);
    const enableBloomReachsubTerm = _get(
        contextData,
        'featureFlags.enableBloomReachsubTerm',
        false
    );
    const data = productBRSearchResults?.data?.suggestionGroups[0];
    return data?.querySuggestions.map((suggestion, index) => {
        const searchTerm = encodeURIComponent(suggestion?.query)?.replaceAll('%20', '+');
        const query = encodeURIComponent(
            productBRSearchResults?.data?.queryContext?.originalQuery?.trim()
        )?.replaceAll('%20', '+');
        return {
            term: suggestion.query,
            url: `/s?searchType=Predictive+Search&searchTerm=${searchTerm}&predSearchTerm=${query}`,
            ...(index === 0 &&
                enableBloomReachsubTerm && {
                    depts: getDepartment(data, searchTerm, query),
                }),
            products: suggestion.products,
        };
    });
}

function* productBRSearchSaga(action) {
    try {
        const productBRSearchResults = yield call(
            getSearchResultsFromBR,
            action.params,
            action.version
        );
        const productSearchResults = yield call(getData, productBRSearchResults);
        yield put({
            type: productSearchActionTypes.PRODUCTSEARCH_GET_SUCCESS_BR,
            productSearchResults,
        });
    } catch (error) {
        yield put({
            type: productSearchActionTypes.PRODUCTSEARCH_GET_ERROR_BR,
            error,
        });
    }
}

function transformConstructorProducts(products) {
    if (!Array.isArray(products) || products.length === 0) return [];
    return products.map((item) => {
        const d = item.data || {};
        const { price } = d;
        const originalPrice = d.original_price;
        let productCurSlotPrice = price ? `$${price}` : null;
        let productOrgSlotPrice =
            originalPrice && originalPrice !== price ? `$${originalPrice}` : null;
        if (d.price_min && d.price_max && d.price_min !== d.price_max) {
            productCurSlotPrice = `$${d.price_min} - $${d.price_max}`;
        }
        if (
            d.original_price_min &&
            d.original_price_max &&
            d.original_price_min !== d.original_price_max
        ) {
            productOrgSlotPrice = `$${d.original_price_min} - $${d.original_price_max}`;
        }
        return {
            id: d.id || '',
            title: d.product_title || item.value || d.product_name || '',
            imageId: d.image_url || '',
            sourceType: d.source || '',
            productUrl: d.url || '',
            averageRating: d.rating ? String(d.rating) : '0',
            totalReviewCount: d.review_count ? String(d.review_count) : '0',
            totalColorCount: d.total_colors ? String(d.total_colors) : '0',
            productCurSlotPrice,
            productOrgSlotPrice,
            productPromoMktgLabel: d.marketing_label || null,
            productMapPriceDesc: null,
            productPromoMessage: null,
        };
    });
}

function getConstructorData(constructorResponse, config = {}) {
    if (!constructorResponse?.data?.sections) {
        return [];
    }
    const searchSuggestionsWithProductsCount = config.searchSuggestionsWithProductsCount || 5;
    const allSuggestions = constructorResponse.data.sections['Search Suggestions'] || [];
    const products = transformConstructorProducts(constructorResponse.data.sections.Products || []);
    const hasProducts = products.length > 0;
    const suggestions = hasProducts
        ? allSuggestions.slice(0, searchSuggestionsWithProductsCount)
        : allSuggestions;
    const originalQuery = constructorResponse.data.request?.term || '';
    const query = encodeURIComponent(originalQuery.trim())?.replaceAll('%20', '+');

    if (suggestions.length === 0 && hasProducts) {
        return [{ products }];
    }

    return suggestions.map((suggestion, index) => {
        const searchTerm = encodeURIComponent(suggestion.value)?.replaceAll('%20', '+');
        return {
            term: suggestion.value,
            url: `/s?searchType=Predictive+Search&searchTerm=${searchTerm}&predSearchTerm=${query}`,
            ...(index === 0 && hasProducts && { products }),
        };
    });
}

function* productConstructorSearchSaga(action) {
    try {
        const contextData = yield select(context);
        const constructorIO = _get(contextData, 'preferences.constructorIO', {});
        const common = constructorIO.common || {};
        const autocomplete = constructorIO.autocomplete || {};
        const { searchTerm, c, i, s, ui } = action.params;

        // Autocomplete timeout — default 10000ms, configurable via preferences.constructorIO.autocomplete.timeout
        const autocompleteTimeout = autocomplete.timeout || 10000;

        const productSuggestionsCount =
            (autocomplete.numSuggestionsPerSection &&
                autocomplete.numSuggestionsPerSection.productSuggestionsCount) ||
            5;
        const searchSuggestionsWithProductsCount =
            (autocomplete.numSuggestionsPerSection &&
                autocomplete.numSuggestionsPerSection.searchSuggestionsWithProductsCount) ||
            5;

        const numSuggestionsPerSection = {
            'Search Suggestions':
                (autocomplete.numSuggestionsPerSection &&
                    autocomplete.numSuggestionsPerSection['Search Suggestions']) ||
                10,
            Products: productSuggestionsCount,
        };

        const { response: constructorResponse, timedOut } = yield race({
            response: call(getConstructorAutocompleteResults, searchTerm, {
                apiUrl: autocomplete.apiUrl || '',
                indexKey: common.indexKey || '',
                numSuggestionsPerSection,
                c,
                i,
                s,
                ui,
            }),
            timedOut: delay(autocompleteTimeout),
        });

        if (timedOut) {
            yield put(
                triggerFormError([
                    {
                        errorDescription: `constructor autocomplete - api call timeout error - term: ${searchTerm} - timeout: ${autocompleteTimeout}ms`,
                    },
                ])
            );
            yield put({
                type: productSearchActionTypes.PRODUCTSEARCH_GET_ERROR_CNSTRC,
                error: new Error(
                    `constructor autocomplete - api call timeout error - term: ${searchTerm} - timeout: ${autocompleteTimeout}ms`
                ),
            });
            return;
        }

        const productSearchResults = getConstructorData(constructorResponse, {
            searchSuggestionsWithProductsCount,
        });

        yield put({
            type: productSearchActionTypes.PRODUCTSEARCH_GET_SUCCESS_CNSTRC,
            productSearchResults,
        });
    } catch (error) {
        yield put({
            type: productSearchActionTypes.PRODUCTSEARCH_GET_ERROR_CNSTRC,
            error,
        });
    }
}

const watchProductSearchRequest = function* watchProductSearchRequest() {
    yield takeLatest(productSearchActionTypes.PRODUCTSEARCH_GET_REQUEST_BR, productBRSearchSaga);
    yield takeLatest(productSearchActionTypes.PRODUCTSEARCH_GET_REQUEST, productSearchSaga);
    yield takeLatest(
        productSearchActionTypes.PRODUCTSEARCH_GET_REQUEST_CNSTRC,
        productConstructorSearchSaga
    );
};

watchProductSearchRequest.sagaName = 'productSearchSaga';

export default watchProductSearchRequest;
